import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { DocumentTemplateTypes } from "../../../../../../../contentManager/Body/MenuItems/useMenuItem_New";
import { GridProps, GridSortProps } from "../../../../../../../contentManager/Body/useContentManagerBody";
import { FilterSectionItemsExpandSearchParams } from "../../../../../../../contentManager/Panels/CMFileFilterPanel/CMFileFilterPanelSection";
import { PersonaUser } from "../../../../../../../contentManager/Panels/CMPermissionsAddMembersPanel";
import { tryDecodeUriComponent } from '../../../../../../../utilities/EncodeDecode';
import { piiConfig } from "../../../../../config/constants";
import { FilterSection, SearchValue } from "../../../../../redux/models/reduxModels";
import { getUserEffectivePermissionsFromMask } from '../../../../../services/SharePointRestServices';
import { CheckinTypes } from "../local/api-local-interfaces";
import { NewDocumentTemplate } from '../../../../../interfaces/IDocumentTemplate';
import { buildLinearRecursiveCAML, escapeXml } from '../../../../../../../contentManager/Panels/CMFileFilterPanel/FileFilterHelper';

const Sharepoint_API_URL = "";
const effectiveBasePermissionsPropName: string = "EffectiveBasePermissions";

export const apiSharePointSlice = createApi({
    reducerPath: 'api-sharepoint',
    baseQuery: fetchBaseQuery({
        baseUrl: Sharepoint_API_URL,
    }),
    endpoints: (builder) => ({
        getListColumns: builder.query<any, { siteUrl: string, sharePointListId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId } = args;
                return {
                    url: `${siteUrl}/_api/web/lists(guid'${sharePointListId}')/fields?$select=Title&$filter=(Title eq '${piiConfig.privacySentitiveColumnName}') or (Title eq '${piiConfig.privacySentitiveDataColumnName}')`,
                    method: 'GET',
                    headers
                }
            },
        }),
        getListColumnsSchema: builder.query<any, { siteUrl: string, sharePointListId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();

                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId } = args;
                return {
                    url: `${siteUrl}/_api/web/lists(guid'${sharePointListId}')/fields?$filter=Hidden eq false`,
                    method: 'GET',
                    headers
                }
            },
        }),
        getListServerRelativeUrl: builder.query<any, { siteUrl: string, sharePointListId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();

                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId } = args;
                return {
                    url: `${siteUrl}/_api/web/lists(guid'${sharePointListId}')/rootFolder?$select=ServerRelativeUrl`,
                    method: 'GET',
                    headers
                }
            },
        }),
        sendSharepointItemToRecyclebin: builder.query<any, { siteUrl: string, sharePointListId: string, accessToken: string, itemId: string }>({
            query: (args) => {
                const headers = new Headers();

                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, itemId } = args;
                return {
                    url: `${siteUrl}/_api/web/lists(guid'${sharePointListId}')/GetItemById(${itemId})/recycle`,
                    method: 'POST',
                    headers
                }
            },
        }),
        renderListAsStream: builder.query<any, { siteUrl: string, sharePointListId: string, viewXml?: string, viewId?: string, gridProps: GridProps, accessToken: string, nextPageUrl?: string, serverRelativePath?: string, filterSections: FilterSection[], includeReplaceGroup?: boolean, searchValue?: SearchValue, filterSectionItemsExpandParams?: FilterSectionItemsExpandSearchParams, isReport?: boolean }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, viewXml, viewId, gridProps, serverRelativePath, filterSections, includeReplaceGroup, searchValue, filterSectionItemsExpandParams, nextPageUrl, isReport } = args;

                let searchValueStr = '';
                if (searchValue?.value?.trim()) {
                    searchValueStr = `&InplaceSearchQuery=(*${searchValue.value.replaceAll("&", "%26")}*)`;
                }

                let overrideViewXml = '';

                const filterStrings: string[] = [];

                const reportCondition: string = isReport
                    ? "<And><Eq><FieldRef Name='_ModerationStatus'/><Value Type='ModStat'>3</Value></Eq><Eq><FieldRef Name='FSObjType'/><Value Type='Integer'>0</Value></Eq></And>"
                    : "";

                let filterFieldCount = 1;
                if (filterSectionItemsExpandParams) {
                    const condition: string = `<Contains id="${filterSectionItemsExpandParams.field}"><FieldRef Name="${filterSectionItemsExpandParams.field}"/><Value Type="Text">${filterSectionItemsExpandParams.value}</Value></Contains>`;
                    if (isReport) {
                        overrideViewXml = `<Query><OrderBy><FieldRef Name="ID" Ascending="FALSE"/></OrderBy><Where><And>${reportCondition}${condition}</And></Where></Query>`;
                    }
                    else {
                        overrideViewXml = `<Query><OrderBy><FieldRef Name="ID" Ascending="FALSE"/></OrderBy><Where>${condition}</Where></Query>`;
                    }
                }
                else {
                    if (filterSections.length > 0) {
                        const combinedQueryClauses: string[] = [];

                        filterSections.forEach(section => {
                            const sectionQueryConditions: string[] = [];

                            if (section.daysOffset || section.daysOffset === 0) {
                                let condition: string;
                                if (section.daysOffset < 0) {
                                    // If the offset is negative, return dates from the past up to today
                                    condition = `<And><Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Geq><Leq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today/></Value></Leq></And>`;
                                } else if (section.daysOffset === 0) {
                                    // If the offset is zero, return results for today
                                    condition = `<Eq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today/></Value></Eq>`;
                                } else if (section.daysOffset === 1) {
                                    // If the offset is 1, return results only for tomorrow
                                    condition = `<Eq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Eq>`;
                                } else if (section.daysOffset > 1 && section.daysOffset <= 92) {
                                    // If the offset is between 1 and 92 (inclusive), return results from tomorrow up to the specified offset
                                    condition = `<And><Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today /></Value></Geq><Leq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Leq></And>`;
                                } else {
                                    // If the offset is 93 or higher, return results from tomorrow onwards
                                    condition = `<Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today /></Value></Geq>`;
                                }

                                if (isReport) {
                                    condition = `<And>${reportCondition}${condition}</And>`;
                                }

                                sectionQueryConditions.push(condition);
                            }
                            else if (section.datasourceMutated) {
                                section.items
                                    .filter(x => x.selected)
                                    .forEach(filterItem => {
                                        sectionQueryConditions.push(`<Contains><FieldRef Name="${escapeXml(section.filterField.RealFieldName)}"/><Value Type="${escapeXml(section.filterField.Type)}">${escapeXml(filterItem.value)}</Value></Contains>`);
                                    });
                            }
                            else if (section.items.some(x => x.selected)) {
                                let selectedCount = section.items.filter(x => x.selected).length;
                                let isWordSelected = section.items.some(x => x.selected && x.fileType === 'docx');
                                let isExcelSelected = section.items.some(x => x.selected && x.fileType === 'xlsx');
                                let isPowerPointSelected = section.items.some(x => x.selected && x.fileType === 'pptx');
                                let isVisioSelected = section.items.some(x => x.selected && x.fileType === 'vsdx');

                                let fieldValues = section.items.filter(x => x.selected).map(item => item.value).join('%3B%23');
                                if (selectedCount > 1) {
                                    fieldValues = fieldValues.replaceAll(";", "%3B%3B");
                                }

                                const filterField = `FilterField${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${section.filterField.RealFieldName}`;
                                const filterValue = `FilterValue${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${fieldValues}`;
                                const filterType = `FilterType${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${section.filterField.Type}`;
                                const filterOp = `FilterOp${filterFieldCount}=In`;
                                filterStrings.push(`${filterField}&${filterValue.replaceAll("&", "%26")}&${filterType}&${filterOp}`);
                                filterFieldCount += 1;
                            }

                            const sectionQueryClause = buildLinearRecursiveCAML(sectionQueryConditions, "Or");
                            if (sectionQueryClause) {
                                combinedQueryClauses.push(sectionQueryClause);
                            }
                        });

                        if (combinedQueryClauses.length > 0) {
                            const whereClause = buildLinearRecursiveCAML(combinedQueryClauses, "And");
                            if (whereClause) {
                                overrideViewXml = `<Query><Where>${whereClause}</Where></Query>`;
                            }
                        }
                    }
                }

                // if (searchValue?.filterType !== "") {
                //     const filterField = `FilterField${searchValue?.filterType !== FilterTypeEnum.PDF ? "s" : ""}${filterFieldCount}=DocIcon`;
                //     const filterValues = `FilterValue${searchValue?.filterType !== FilterTypeEnum.PDF ? "s" : ""}${filterFieldCount}=${SearchFilterBuilderExtensions(searchValue?.filterType).join('%3B%23')}`;
                //     const filterType = `FilterType${searchValue?.filterType !== FilterTypeEnum.PDF ? "s" : ""}${filterFieldCount}=Computed`;
                //     const filterOp = `FilterOp${filterFieldCount}=In`;
                //     filterStrings.push(`${filterField}&${filterValues.replaceAll("&", "%26")}&${filterType}&${filterOp}`);
                // }

                const filterFieldsString = `${filterStrings.length > 0 ? '&' : ''}${filterStrings.join('&')}`;

                let viewIdurl = viewId ? `View=${viewId}` : '';

                let sortFields = buildSortByUrl(gridProps.sort);

                let body = {
                    parameters: {
                        DatesInUtc: true,
                        ViewXml: '',
                        OverrideViewXml: '',
                        AddRequiredFields: true,
                        AllowMultipleValueFilterForTaxonomyFields: true,
                        RenderOptions: 1445895
                    }
                }

                if (isReport && !overrideViewXml) {
                    overrideViewXml = `<Query><OrderBy><FieldRef Name="ID" Ascending="FALSE"/></OrderBy><Where>${reportCondition}</Where></Query>`;
                }

                if (overrideViewXml) {
                    body.parameters.OverrideViewXml = overrideViewXml;
                    delete body.parameters.ViewXml;
                } else {
                    body.parameters.ViewXml = viewXml;
                    delete body.parameters.OverrideViewXml;
                }

                if (serverRelativePath) {
                    body.parameters['FolderServerRelativeUrl'] = tryDecodeUriComponent(serverRelativePath);
                }

                //To bypass SP throttling - SPQueryThrottledException
                if (isReport || includeReplaceGroup === true) {
                    body.parameters['ReplaceGroup'] = true;
                }

                let url = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream?${viewIdurl}${sortFields}${filterFieldsString}${searchValueStr}`;
                if (nextPageUrl) {
                    url = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream${nextPageUrl}`;
                }

                return {
                    url,
                    method: 'POST',
                    headers,
                    body
                }
            },
            transformResponse: (response: any) => {
                for (let i = 0, n = response.ListData.Row.length; i < n; ++i) {
                    response.ListData.Row[i][effectiveBasePermissionsPropName] = getUserEffectivePermissionsFromMask(response.ListData.Row[i].PermMask);
                }
                return response;
            }
        }),

        renderListAsStreamTree: builder.query<any, { siteUrl: string, sharePointListId: string, accessToken: string, nextPageUrl?: string, serverRelativePath?: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, serverRelativePath, nextPageUrl } = args;

                let body = {
                    parameters: {
                        DatesInUtc: true,
                        OverrideViewXml: "<View><RowLimit>50</RowLimit></View>",
                        RenderOptions: 1445895,
                        ReplaceGroup: true
                    }
                }

                if (serverRelativePath) {
                    body.parameters['FolderServerRelativeUrl'] = tryDecodeUriComponent(serverRelativePath);
                }

                let filterField = "FSObjType";
                let filterValue = 1;

                let url = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream?SortField=ID&SortDir=Desc&FilterField1=${filterField}&FilterValue1=${filterValue}`;
                if (nextPageUrl) {
                    url = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream${nextPageUrl}`;
                }

                return {
                    url,
                    method: 'POST',
                    headers,
                    body
                }
            },
            transformResponse: (response: any) => {
                for (let i = 0, n = response.ListData.Row.length; i < n; ++i) {
                    response.ListData.Row[i][effectiveBasePermissionsPropName] = getUserEffectivePermissionsFromMask(response.ListData.Row[i].PermMask);
                }
                return response;
            }
        }),

        renderListAsStreamByID: builder.query<any, { siteUrl: string, sharePointListId: string, viewId?: string, accessToken: string, Id: string, serverRelativePath?: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, viewId, Id, serverRelativePath } = args;

                let viewIdurl = viewId ? `View=${viewId}` : '';

                const body = {
                    parameters: {
                        AddRequiredFields: true,
                        DatesInUtc: true,
                        RenderOptions: 1445895,
                        ReplaceGroup: true
                    }
                }

                if (serverRelativePath) {
                    body.parameters['FolderServerRelativeUrl'] = tryDecodeUriComponent(serverRelativePath);
                }

                return {
                    url: `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream?${viewIdurl}&SortDir=Desc&FilterField1=ID&FilterValue1=${Id}`,
                    method: 'POST',
                    headers,
                    body
                }
            },
            transformResponse: (response: any) => {
                for (let i = 0, n = response.ListData.Row.length; i < n; ++i) {
                    response.ListData.Row[i][effectiveBasePermissionsPropName] = getUserEffectivePermissionsFromMask(response.ListData.Row[i].PermMask);
                }
                return response;
            }
        }),

        renderListAsStreamGroupDrilldown: builder.query<any, { siteUrl: string, sharePointListId: string, viewId?: string, groupString: string, sortProps: GridSortProps[], accessToken: string, serverRelativePath?: string, nextHref: string, filterField?: string, filterValue?: string, filterSections: FilterSection[], searchValue?: SearchValue }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, viewId, groupString, sortProps, serverRelativePath, nextHref, filterField, filterValue, filterSections, searchValue } = args;

                let viewIdurl = viewId ? `View=${viewId}` : '';
                let sortFields = buildSortByUrl(sortProps);

                let overrideViewXml = '';

                const filterStrings: string[] = [];
                let filterFieldCount = 1;
                if (filterSections.length > 0) {
                    filterSections.forEach(section => {
                        if (section.daysOffset || section.daysOffset === 0) {
                            overrideViewXml = `<View><Query><Where><Geq id=\"${section.filterField.RealFieldName}\"><FieldRef Name=\"${section.filterField.RealFieldName}\"/><Value Type=\"${section.filterField.Type}\"><Today OffsetDays=\"${section.daysOffset}\"/></Value></Geq></Where></Query></View>`
                        } else {
                            if (section.items.some(x => x.selected)) {
                                let selectedCount = section.items.filter(x => x.selected).length;
                                let isWordSelected = section.items.some(x => x.selected && x.fileType === 'docx');
                                let isExcelSelected = section.items.some(x => x.selected && x.fileType === 'xlsx');
                                let isPowerPointSelected = section.items.some(x => x.selected && x.fileType === 'pptx');
                                let isVisioSelected = section.items.some(x => x.selected && x.fileType === 'vsdx');

                                const fieldValues = section.items.filter(x => x.selected).map(item => item.value).join('%3B%23');
                                const filterField = `FilterField${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${section.filterField.RealFieldName}`;
                                const filterValue = `FilterValue${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${fieldValues}`;
                                const filterType = `FilterType${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${section.filterField.Type}`;
                                const filterOp = `FilterOp${filterFieldCount}=In`;
                                filterStrings.push(`${filterField}&${filterValue.replaceAll("&", "%26")}&${filterType}&${filterOp}`);
                                filterFieldCount += 1;
                            }
                        }
                    });
                }

                const filterFieldsString = `${filterStrings.length > 0 ? '&' : ''}${filterStrings.join('&')}`;

                const searchValueStr = searchValue?.value?.trim().length > 0 ? `&InplaceSearchQuery=(*${searchValue.value.replaceAll("&", "%26")}*)` : "";

                // let url = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream?${viewIdurl}&IsGroupRender=TRUE&DrillDown=1&GroupString=${groupString}${sortFields}${filterFieldsString}${searchValueStr}`;

                // if (nextHref) {
                //     url = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream${nextHref}`;

                //     let updatedURL = new URL(url);
                //     let params = new URLSearchParams(updatedURL.search);
                //     params.delete('GroupString');
                //     params.delete('DrillDown');
                //     params.append('FilterField1', decodeURI(filterField));
                //     params.append('FilterValue1', decodeURI(filterValue));

                //     url = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream?${params.toString()}`;
                // }
                let url: string;
                if (!nextHref) {
                    url = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream?${viewIdurl}&IsGroupRender=TRUE&DrillDown=1&GroupString=${groupString}${sortFields}${filterFieldsString}${searchValueStr}`;
                }
                else {
                    const groupPagedUrl = new URL(`${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream${nextHref}`);
                    groupPagedUrl.searchParams.append("IsGroupRender", "TRUE");
                    url = groupPagedUrl.href;
                }

                const body = {
                    parameters: {
                        DatesInUtc: true,
                        AddRequiredFields: true,
                        AllowMultipleValueFilterForTaxonomyFields: true,
                        RenderOptions: 4103
                    }
                }

                if (serverRelativePath) {
                    body.parameters['FolderServerRelativeUrl'] = tryDecodeUriComponent(serverRelativePath);
                }

                return {
                    url,
                    method: 'POST',
                    headers,
                    body
                }
            },
            transformResponse: (response: any) => {
                for (let i = 0, n = response.ListData.Row.length; i < n; ++i) {
                    response.ListData.Row[i][effectiveBasePermissionsPropName] = getUserEffectivePermissionsFromMask(response.ListData.Row[i].PermMask);
                }
                return response;
            }
        }),

        renderListAsStreamGroupDrilldown2: builder.query<any, { siteUrl: string, sharePointListId: string, viewId?: string, sortProps: GridSortProps[], accessToken: string, serverRelativePath?: string, nextHref: string, filterField?: string, filterValue?: string, searchValue?: SearchValue }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, viewId, sortProps, serverRelativePath, nextHref, filterField, filterValue, searchValue } = args;

                let viewIdurl = viewId ? `View=${viewId}` : '';
                let sortFields = buildSortByUrl(sortProps);

                const searchValueStr = searchValue?.value?.trim().length > 0 ? `&InplaceSearchQuery=(*${searchValue.value.replaceAll("&", "%26")}*)` : "";

                let url = ``
                if (!nextHref) {
                    url = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream?${viewIdurl}&FilterField1=${filterField}&FilterValue1=${filterValue}${sortFields}${searchValueStr}`;
                } else {
                    url = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream${nextHref}`;
                }

                const body = {
                    parameters: {
                        DatesInUtc: true,
                        AddRequiredFields: true,
                        AllowMultipleValueFilterForTaxonomyFields: true,
                        RenderOptions: 4103
                    }
                }

                if (serverRelativePath) {
                    body.parameters['FolderServerRelativeUrl'] = tryDecodeUriComponent(serverRelativePath);
                }

                return {
                    url,
                    method: 'POST',
                    headers,
                    body
                }
            },
            transformResponse: (response: any) => {
                for (let i = 0, n = response.ListData.Row.length; i < n; ++i) {
                    response.ListData.Row[i][effectiveBasePermissionsPropName] = getUserEffectivePermissionsFromMask(response.ListData.Row[i].PermMask);
                }
                return response;
            }
        }),
        renderListAsStreamNextPage: builder.query<any, { siteUrl: string, sharePointListId: string, nextPageUrl: string, accessToken: string, filterSections: FilterSection[], searchValue?: SearchValue }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, nextPageUrl, filterSections, searchValue } = args;

                let overrideViewXml = '';

                if (filterSections.length > 0) {
                    filterSections.forEach(section => {

                        if (section.daysOffset || section.daysOffset === 0) {
                            if (section.daysOffset < 0) {
                                // If the offset is negative, return dates from the past up to today
                                overrideViewXml = `<View><Query><Where><And><Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Geq><Leq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today/></Value></Leq></And></Where></Query></View>`;
                            } else if (section.daysOffset === 0) {
                                // If the offset is zero, return results for today
                                overrideViewXml = `<View><Query><Where><Eq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today/></Value></Eq></Where></Query></View>`;
                            } else if (section.daysOffset === 1) {
                                // If the offset is 1, return results only for tomorrow
                                overrideViewXml = `<View><Query><Where><Eq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today /></Value></Eq></Where></Query></View>`;
                            } else if (section.daysOffset > 1 && section.daysOffset <= 92) {
                                // If the offset is between 1 and 92 (inclusive), return results from tomorrow up to the specified offset
                                overrideViewXml = `<View><Query><Where><And><Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today /></Value></Geq><Leq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Leq></And></Where></Query></View>`;
                            } else {
                                // If the offset is 93 or higher, return results from tomorrow onwards
                                overrideViewXml = `<View><Query><Where><Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today /></Value></Geq></Where></Query></View>`;
                            }
                        }
                    });
                }

                const body = {
                    parameters: {
                        DatesInUtc: true,
                        AddRequiredFields: true,
                        OverrideViewXml: '',
                        AllowMultipleValueFilterForTaxonomyFields: true,
                        RenderOptions: 1445895
                    }
                }

                if (overrideViewXml) {
                    body.parameters.OverrideViewXml = overrideViewXml;
                } else {
                    delete body.parameters.OverrideViewXml;
                }

                let url: string = `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream${nextPageUrl}`;
                if (searchValue?.value?.trim()) {
                    url += `&InplaceSearchQuery=(*${searchValue.value.replaceAll("&", "%26")}*)`;
                }

                return {
                    url,
                    method: 'POST',
                    headers,
                    body
                }
            },
            transformResponse: (response: any) => {
                for (let i = 0, n = response.ListData.Row.length; i < n; ++i) {
                    response.ListData.Row[i][effectiveBasePermissionsPropName] = getUserEffectivePermissionsFromMask(response.ListData.Row[i].PermMask);
                }
                return response;
            }
        }),
        renderListAsStreamSearch: builder.query<any, { siteUrl: string, sharePointListId: string, viewId?: string, searchValue: string, accessToken: string, serverRelativePath: string, sortProps: GridSortProps[] }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json; odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, viewId, searchValue, serverRelativePath, sortProps } = args;

                let viewIdurl = viewId ? `View=${viewId}` : '';

                const body = {
                    parameters: {
                        DatesInUtc: true,
                        AddRequiredFields: true,
                        AllowMultipleValueFilterForTaxonomyFields: true,
                        FirstGroupOnly: true,
                        OverrideViewXml: "<View><RowLimit>100</RowLimit></View>",
                        RenderOptions: 4103
                    }
                }

                if (serverRelativePath) {
                    body.parameters['FolderServerRelativeUrl'] = tryDecodeUriComponent(serverRelativePath);
                }

                return {
                    url: `${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream?${viewIdurl}&InplaceSearchQuery=(*${searchValue.replaceAll("&", "%26")}*)${buildSortByUrl(sortProps)}`,
                    method: 'POST',
                    headers,
                    body
                }
            },
            transformResponse: (response: any) => {
                for (let i = 0, n = response.ListData.Row.length; i < n; ++i) {
                    response.ListData.Row[i][effectiveBasePermissionsPropName] = getUserEffectivePermissionsFromMask(response.ListData.Row[i].PermMask);
                }
                return response;
            }
        }),
        getItemByUniqueID: builder.query<any, { siteUrl: string, sharePointListId: string, uniqueId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, uniqueId } = args;

                return {
                    url: `${siteUrl}/_api/web/lists('${sharePointListId}')/GetItemByUniqueId('${uniqueId}')`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getFileByID: builder.query<any, { siteUrl: string, uniqueId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, uniqueId } = args;

                return {
                    url: `${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getItemBySPItemURL: builder.query<any, { spItemUrl: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                let { spItemUrl } = args;

                spItemUrl = spItemUrl.replace('v2.0', 'v2.1')

                return {
                    url: spItemUrl,
                    method: 'GET',
                    headers
                }
            }
        }),
        getItemByItemAndDriveID: builder.query<any, { siteUrl: string, driveId: string, itemId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                let { siteUrl, driveId, itemId } = args;

                return {
                    url: `${siteUrl}/_api/v2.1/drives/${driveId}/items/${itemId}?%24select=*`,
                    method: 'GET',
                    headers
                }
            }
        }),
        checkoutItem: builder.query<any, { siteUrl: string, uniqueId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, uniqueId } = args;

                return {
                    url: `${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')/checkout`,
                    method: 'POST',
                    headers
                }
            }
        }),
        checkinItem: builder.query<any, { siteUrl: string, uniqueId: string, accessToken: string, checkinType: CheckinTypes, comment?: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, uniqueId, checkinType, comment } = args;

                return {
                    url: `${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')/checkin(comment='${comment}',checkintype=${checkinType})`,
                    method: 'POST',
                    headers
                }
            }
        }),
        publishItem: builder.query<any, { siteUrl: string, uniqueId: string, accessToken: string, comment?: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, uniqueId, comment } = args;

                return {
                    url: `${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')/publish(comment='${comment}')`,
                    method: 'POST',
                    headers
                }
            }
        }),
        unpublishItem: builder.query<any, { siteUrl: string, uniqueId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, uniqueId } = args;

                return {
                    url: `${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')/unpublish(comment='')`,
                    method: 'POST',
                    headers
                }
            }
        }),
        undoCheckout: builder.query<any, { siteUrl: string, uniqueId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, uniqueId } = args;

                return {
                    url: `${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')/undocheckout`,
                    method: 'POST',
                    headers
                }
            }
        }),
        getItemVersions: builder.query<any, { siteUrl: string, uniqueId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json; odata=verbose`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, uniqueId } = args;

                return {
                    url: `${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')/versions`,
                    method: 'POST',
                    headers
                }
            }
        }),
        getItemByUniqueIDVersions: builder.query<any, { siteUrl: string, sharePointListId: string, uniqueId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, uniqueId } = args;

                return {
                    url: `${siteUrl}/_api/web/lists('${sharePointListId}')/GetItemByUniqueId('${uniqueId}')/versions`,
                    method: 'GET',
                    headers
                }
            }
        }),
        restoreVersionByLabel: builder.query<any, { siteUrl: string, uniqueId: string, accessToken: string, versionLabel: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Accept', `application/json;odata=nometadata`);
                headers.set('Content-Type', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('IF-MATCH', `*`);
                headers.set('X-HTTP-Method', `PATCH`);

                const { siteUrl, uniqueId, versionLabel } = args;

                return {
                    url: `${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')/versions/RestoreByLabel('${versionLabel}')`,
                    method: 'POST',
                    headers
                }
            }
        }),
        deleteVersionByLabel: builder.query<any, { siteUrl: string, uniqueId: string, accessToken: string, versionLabel: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Accept', `application/json;odata=nometadata`);
                headers.set('Content-Type', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('IF-MATCH', `*`);
                headers.set('X-HTTP-Method', `DELETE`);

                const { siteUrl, uniqueId, versionLabel } = args;

                return {
                    url: `${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')/versions/DeleteByLabel('${versionLabel}')`,
                    method: 'POST',
                    headers
                }
            }
        }),
        deleteAllVersions: builder.query<any, { siteUrl: string, uniqueId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Accept', `application/json;odata=nometadata`);
                headers.set('Content-Type', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('IF-MATCH', `*`);
                headers.set('X-HTTP-Method', `DELETE`);

                const { siteUrl, uniqueId } = args;

                return {
                    url: `${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')/versions/DeleteAll()`,
                    method: 'POST',
                    headers
                }
            }
        }),
        bulkValidateUpdateListItems: builder.query<any, { siteUrl: string, sharePointListId: string, accessToken: string, status: string, comments: string, itemId: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, status, comments, itemId } = args;

                const body = {
                    folderPath: '',
                    formValues: [
                        { FieldName: "_ModerationStatus", FieldValue: status },
                        { FieldName: "_ModerationComments", FieldValue: comments }
                    ],
                    itemIds: [itemId]
                }

                return {
                    url: `${siteUrl}/_api/web/lists('${sharePointListId}')/BulkValidateUpdateListItems()`,
                    method: 'POST',
                    headers,
                    body
                }
            }
        }),
        createDocumentAndGetEditLink: builder.query<any, { siteUrl: string, sharePointListId: string, accessToken: string, folderPath: string, template: NewDocumentTemplate, filename: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, folderPath, template, filename } = args;

                let fileWithExtention = filename.replaceAll("'", "''");
                if (template.documentTemplateType !== DocumentTemplateTypes.one) {
                    fileWithExtention = `${filename}.${template.extention}`
                }

                return {
                    url: `${siteUrl}/_api/web/lists('${sharePointListId}')/CreateDocumentAndGetEditLink(fileName='${fileWithExtention}',folderPath='${folderPath}',documentTemplateType=${template.documentTemplateType},templateUrl='${template.url ? template.url : ''}')`,
                    method: 'POST',
                    headers,
                }
            }
        }),
        getSiteExpandAuthor: builder.query<any, { siteUrl: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl } = args;

                return {
                    url: `${siteUrl}/_api/web?$expand=Author`,
                    method: 'GET',
                    headers,
                }
            }
        }),
        getSiteGroupUsers: builder.query<any, { siteUrl: string, accessToken: string, groupId: number }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, groupId } = args;

                return {
                    url: `${siteUrl}/_api/web/SiteGroups/GetById(id=${groupId})?$expand=Users`,
                    method: 'GET',
                    headers,
                }
            }
        }),
        getDocumentLibraryGroupUsers: builder.query<any, { siteUrl: string, accessToken: string, sharePointListId: string, groupId: number }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, groupId, sharePointListId } = args;

                return {
                    url: `${siteUrl}/_api/web/lists('${sharePointListId}')/RoleAssignments/GetByPrincipalId(${groupId})/Member?$expand=Users`,
                    method: 'GET',
                    headers,
                }
            }
        }),
        breakRoleInheritanceOfList: builder.query<any, { siteUrl: string, accessToken: string, sharePointListId: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId } = args;

                return {
                    url: `${siteUrl}/_api/web/lists('${sharePointListId}')/breakroleinheritance(true)`,
                    method: 'POST',
                    headers,
                }
            }
        }),
        deleteLibraryCurrentRoleForGroup: builder.query<any, { siteUrl: string, accessToken: string, sharePointListId: string, userId: number, roleDefId: number }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, userId, roleDefId } = args;

                let url = `${siteUrl}/_api/web/lists('${sharePointListId}')/roleassignments/removeroleassignment(principalid=${userId}, roledefid=${roleDefId})`;
                if (!sharePointListId) {
                    url = `${siteUrl}/_api/web/roleassignments/removeroleassignment(principalid=${userId}, roledefid=${roleDefId})`;
                }

                return {
                    url,
                    method: 'POST',
                    headers,
                }
            }
        }),
        addLibraryCurrentRoleForGroup: builder.query<any, { siteUrl: string, accessToken: string, sharePointListId: string, userId: number, roleDefId: number }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, userId, roleDefId } = args;

                let url = `${siteUrl}/_api/web/lists('${sharePointListId}')/roleassignments/addroleassignment(principalid=${userId}, roledefid=${roleDefId})`;
                if (!sharePointListId) {
                    url = `${siteUrl}/_api/web/roleassignments/addroleassignment(principalid=${userId}, roledefid=${roleDefId})`;
                }

                return {
                    url,
                    method: 'POST',
                    headers,
                }
            }
        }),
        getSiteRoleAssignments: builder.query<any, { siteUrl: string, accessToken: string, sharePointListId?: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId } = args;

                let url = `${siteUrl}/_api/web/roleassignments?$expand=Member/users,RoleDefinitionBindings`;

                if (sharePointListId) {
                    url = `${siteUrl}/_api/web/lists('${sharePointListId}')/roleassignments?$expand=Member/users,RoleDefinitionBindings`;
                }

                //let url = `${siteUrl}/_api/web/lists('${sharePointListId}')/roleassignments?$expand=Member/users,RoleDefinitionBindings`;

                return {
                    url,
                    method: 'GET',
                    headers,
                }
            }
        }),
        getRoleDefinitions: builder.query<any, { siteUrl: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, } = args;

                let url = `${siteUrl}/_api/web/RoleDefinitions`;

                return {
                    url,
                    method: 'GET',
                    headers,
                }
            }
        }),
        moveUsertoSiteGroup: builder.query<any, { siteUrl: string, accessToken: string, groupId: number, loginName: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, groupId, loginName } = args;

                const body = {
                    LoginName: loginName
                }

                return {
                    url: `${siteUrl}/_api/web/sitegroups(id=${groupId})/users`,
                    method: 'POST',
                    headers,
                    body
                }
            }
        }),
        addUsersToGroup: builder.query<any, { siteUrl: string, accessToken: string, roleValue: string, users: PersonaUser[], loginNameTypeOveride?: string, sharePointListId?: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, roleValue, users, loginNameTypeOveride, sharePointListId } = args;

                let spListObjStr = '';
                if (sharePointListId && roleValue.includes('role')) {
                    spListObjStr = `?obj={${sharePointListId}},DOCLIB&List=%7B${sharePointListId}%7D`
                }

                let peoplePickerInputStr = '';
                if (users[0].isGroup) {
                    peoplePickerInputStr = `[{\"Key\":\"${loginNameTypeOveride ? loginNameTypeOveride : 'i:0#.f|tenant'}|${users[0].userGuid}\",\"IsResolved\":true}]`
                } else {
                    peoplePickerInputStr = `[{\"Key\":\"i:0#.f|membership|${users[0].email.toLowerCase()}\",\"IsResolved\":true}]`
                }


                const body = {
                    propagateAcl: false,
                    emailBody: "",
                    emailSubject: "",
                    groupId: 0,
                    includeAnonymousLinkInEmail: false,
                    peoplePickerInput: peoplePickerInputStr,
                    roleValue,
                    sendEmail: false,
                    url: `${siteUrl}${spListObjStr}`
                }

                return {
                    url: `${siteUrl}/_api/SP.Web.ShareObject?$expand=UsersWithAccessRequests,GroupsSharedWith`,
                    method: 'POST',
                    headers,
                    body
                }
            }
        }),
        removeUserFromSiteGroup: builder.query<any, { siteUrl: string, accessToken: string, groupId: number, userId: number }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, groupId, userId } = args;


                return {
                    url: `${siteUrl}/_api/web/sitegroups(id=${groupId})/users/removebyid(id=${userId})`,
                    method: 'POST',
                    headers
                }
            }
        }),
        getSecurityGroupMembersOwners: builder.query<any, { siteUrl: string, accessToken: string, groupGuid: string, userType: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, groupGuid, userType } = args;

                return {
                    url: `${siteUrl}/_api/SP.Directory.DirectorySession/Group('${groupGuid}')/${userType}?$skip=0&$top=20&$inlinecount=allpages&$select=PrincipalName,Id,DisplayName,PictureUrl,UserType,Mail`,
                    method: 'GET',
                    headers,
                }
            }
        }),
        getCopyJobProgress: builder.query<any, { siteUrl: string, accessToken: string, copyJobInfo: any }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json;odata=nometadata`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, copyJobInfo } = args;

                const body = {
                    copyJobInfo
                }

                return {
                    url: `${siteUrl}/_api/site/GetCopyJobProgress`,
                    method: 'POST',
                    headers,
                    body
                }
            }
        }),
        getSharingInformation: builder.query<any, { siteUrl: string, sharePointListId: string, uniqueId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl, sharePointListId, uniqueId } = args;

                return {
                    url: `${siteUrl}/_api/web/lists('${sharePointListId}')/GetItemByUniqueId('${uniqueId}')/GetSharingInformation?$select=permissionsInformation&$Expand=permissionsInformation`,
                    method: 'POST',
                    headers
                }
            }
        }),
        getFollowedSites: builder.query<any, { siteUrl: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('accept', `application/json`);
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { siteUrl } = args;

                return {
                    url: `${siteUrl}/_api/social.following/my/followed(types=4)`,
                    method: 'GET',
                    headers
                }
            }
        }),
    })

});

const buildSortByUrl = (sortProps: GridSortProps[]) => {
    if (sortProps?.length === 1) {
        const direction = sortProps[0].decending ? 'Desc' : 'Asc'

        return `&SortField=${sortProps[0].field}&SortDir=${direction}`;
    }
    else if (sortProps?.length === 1) {
        const directionField1 = sortProps[0].decending ? 'Desc' : 'Asc'
        const directionField2 = sortProps[1].decending ? 'Desc' : 'Asc'

        return `&SortField=${sortProps[0].field}&SortDir=${directionField1}&SortField=${sortProps[1].field}&SortDir=${directionField2}`;
    }

    return '';
}

export const {
    useLazyGetListColumnsQuery,
    useLazyGetListColumnsSchemaQuery,
    useLazyRenderListAsStreamQuery,
    useLazyRenderListAsStreamGroupDrilldownQuery,
    useLazyRenderListAsStreamGroupDrilldown2Query,
    useLazyRenderListAsStreamNextPageQuery,
    useLazyRenderListAsStreamSearchQuery,
    useLazyGetItemByUniqueIDQuery,
    useLazyGetListServerRelativeUrlQuery,
    useLazyRenderListAsStreamByIDQuery,
    useLazySendSharepointItemToRecyclebinQuery,
    useLazyGetItemBySPItemURLQuery,
    useLazyGetItemByItemAndDriveIDQuery,
    useLazyGetFileByIDQuery,
    useLazyCheckoutItemQuery,
    useLazyCheckinItemQuery,
    useLazyUnpublishItemQuery,
    useLazyPublishItemQuery,
    useLazyUndoCheckoutQuery,
    useLazyGetItemVersionsQuery,
    useLazyGetItemByUniqueIDVersionsQuery,
    useLazyRestoreVersionByLabelQuery,
    useLazyDeleteVersionByLabelQuery,
    useLazyDeleteAllVersionsQuery,
    useLazyBulkValidateUpdateListItemsQuery,
    useLazyCreateDocumentAndGetEditLinkQuery,
    useLazyGetSiteExpandAuthorQuery,
    useLazyGetSiteGroupUsersQuery,
    useLazyGetSiteRoleAssignmentsQuery,
    useLazyMoveUsertoSiteGroupQuery,
    useLazyAddUsersToGroupQuery,
    useLazyRemoveUserFromSiteGroupQuery,
    useLazyGetDocumentLibraryGroupUsersQuery,
    useLazyAddLibraryCurrentRoleForGroupQuery,
    useLazyDeleteLibraryCurrentRoleForGroupQuery,
    useLazyBreakRoleInheritanceOfListQuery,
    useLazyGetRoleDefinitionsQuery,
    useLazyGetSecurityGroupMembersOwnersQuery,
    useLazyGetCopyJobProgressQuery,
    useLazyGetSharingInformationQuery,
    useLazyGetFollowedSitesQuery,
    useLazyRenderListAsStreamTreeQuery
} = apiSharePointSlice;